import {
  FunctionComponent,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { modelsService, FilteredModel } from '../../services/modelsService';
import { useChatSettingsStore } from '@/store/chatSettingsStore';
import styles from '../../styles/components/chat/SettingsBar.module.css';

interface SettingsBarProps {
  isOpen: boolean;
  onClose: () => void;
  anchorRight?: number;
  anchorBottom?: number;
}

const SettingsBar: FunctionComponent<SettingsBarProps> = ({
  isOpen,
  onClose,
  anchorRight,
  anchorBottom,
}) => {
  const [availableModels, setAvailableModels] = useState<FilteredModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const modelsLoadedRef = useRef(false);
  const mountCountRef = useRef(0);
  const storeSelectedModelId = useChatSettingsStore(s => s.selectedModelId);
  const setSelectedModelId = useChatSettingsStore(s => s.setSelectedModelId);
  const [selectedModel, setSelectedModel] = useState('Select option');
  const [selectedTools, setSelectedTools] = useState('Select option');
  const systemPrompt = useChatSettingsStore(s => s.systemPrompt);
  const setSystemPrompt = useChatSettingsStore(s => s.setSystemPrompt);
  const storeTopP = useChatSettingsStore(s => s.topP);
  const setTopP = useChatSettingsStore(s => s.setTopP);
  const storeTopK = useChatSettingsStore(s => s.topK);
  const setTopK = useChatSettingsStore(s => s.setTopK);
  const storeTemperature = useChatSettingsStore(s => s.temperature);
  const setTemperature = useChatSettingsStore(s => s.setTemperature);

  // Local state for settings in the modal
  const [localSelectedModelId, setLocalSelectedModelId] = useState<
    string | null
  >(null);
  const [localTopP, setLocalTopP] = useState(0.9);
  const [localTopK, setLocalTopK] = useState(40);
  const [localTemperature, setLocalTemperature] = useState(0.7);
  const [gpuLayers, setGpuLayers] = useState(32);
  const contextSize = useChatSettingsStore(s => s.contextSize);
  const setContextSize = useChatSettingsStore(s => s.setContextSize);

  const [functionSupport, setFunctionSupport] = useState(false);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [topGradientOpacity, setTopGradientOpacity] = useState(0);
  const [bottomGradientOpacity, setBottomGradientOpacity] = useState(1);
  const [modalPosition, setModalPosition] = useState({
    bottom: 120,
    maxHeight: 600,
  });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);
  const toolsDropdownRef = useRef<HTMLDivElement>(null);
  const settingsBarRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Track original settings to compare with current values
  const [originalSettings, setOriginalSettings] = useState<{
    selectedModelId: string | null;
    topP: number;
    topK: number;
    temperature: number;
  } | null>(null);

  const toolsList = [
    'Search',
    'File Upload',
    'Image Generation',
    'Data Analysis',
    'Code Execution',
    'Web Scraping',
    'Calculator',
    'Translation',
  ];

  // Check if current settings differ from original settings
  const hasSettingsChanged = useCallback(() => {
    if (!originalSettings) return false;

    return (
      localSelectedModelId !== originalSettings.selectedModelId ||
      localTopP !== originalSettings.topP ||
      localTopK !== originalSettings.topK ||
      localTemperature !== originalSettings.temperature
    );
  }, [
    originalSettings,
    localSelectedModelId,
    localTopP,
    localTopK,
    localTemperature,
  ]);

  // Handle apply button click
  const handleApply = useCallback(() => {
    if (!hasSettingsChanged()) return;

    // Update the store with local values
    if (localSelectedModelId !== null) {
      setSelectedModelId(localSelectedModelId);
    }
    setTopP(localTopP);
    setTopK(localTopK);
    setTemperature(localTemperature);

    // Update original settings to current values
    setOriginalSettings({
      selectedModelId: localSelectedModelId,
      topP: localTopP,
      topK: localTopK,
      temperature: localTemperature,
    });

    // Close the settings bar
    onClose();
  }, [
    hasSettingsChanged,
    localSelectedModelId,
    localTopP,
    localTopK,
    localTemperature,
    setSelectedModelId,
    setTopP,
    setTopK,
    setTemperature,
    onClose,
  ]);

  // Handle scroll to update gradient opacity
  const handleScroll = useCallback(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;
    const maxScroll = scrollHeight - clientHeight;

    // Calculate opacity based on scroll position (0 to 1)
    // Top gradient: fade in when scrolling down from top
    const topOpacity = Math.min(scrollTop / 60, 1); // Fade in over first 60px for smoother effect

    // Bottom gradient: fade in when approaching bottom
    const bottomOpacity = Math.min((maxScroll - scrollTop) / 60, 1); // Fade in over last 60px

    setTopGradientOpacity(topOpacity);
    setBottomGradientOpacity(bottomOpacity);
  }, []);

  // Update slider fill based on current value
  const updateSliderFill = useCallback(
    (slider: HTMLInputElement, value: number, min: number, max: number) => {
      const percentage = ((value - min) / (max - min)) * 100;
      slider.style.background = `linear-gradient(to right, var(--primary-base) 0%, var(--primary-base) ${percentage}%, var(--surface-surface_2) ${percentage}%, var(--surface-surface_2) 100%)`;
    },
    []
  );

  // Calculate optimal modal position to stay within viewport
  const calculateModalPosition = useCallback(() => {
    const viewportHeight = window.innerHeight;
    const modalHeight = 600; // Default modal height
    const minBottomMargin = 20; // Minimum margin from bottom

    // Calculate available space
    const availableHeight = viewportHeight - minBottomMargin;

    // If viewport is too small, adjust position and height
    if (availableHeight < modalHeight) {
      const newMaxHeight = availableHeight - 40; // Leave some padding
      const newBottom = minBottomMargin;
      setModalPosition({ bottom: newBottom, maxHeight: newMaxHeight });
    } else {
      // Use default positioning
      setModalPosition({ bottom: 120, maxHeight: modalHeight });
    }
  }, []);

  // Close dropdowns and settings bar when clicking outside
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      // Close dropdowns when clicking outside them
      if (
        modelDropdownRef.current &&
        !modelDropdownRef.current.contains(event.target as Node)
      ) {
        setIsModelDropdownOpen(false);
      }
      if (
        toolsDropdownRef.current &&
        !toolsDropdownRef.current.contains(event.target as Node)
      ) {
        setIsToolsDropdownOpen(false);
      }

      // Close settings bar when clicking outside it
      if (
        settingsBarRef.current &&
        !settingsBarRef.current.contains(event.target as Node)
      ) {
        // Auto-save changes when clicking outside if there are any
        if (hasSettingsChanged()) {
          handleApply();
        } else {
          onClose();
        }
      }
    },
    [onClose, hasSettingsChanged, handleApply]
  );

  // Handle escape key to close settings
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Auto-save changes when pressing escape if there are any
        if (hasSettingsChanged()) {
          handleApply();
        } else {
          onClose();
        }
      }
    },
    [onClose, hasSettingsChanged, handleApply]
  );

  useEffect(() => {
    const loadModels = async () => {
      // Prevent multiple calls if models are already loaded or loading
      if (modelsLoadedRef.current || isLoadingModels) {
        return;
      }

      setIsLoadingModels(true);
      try {
        const models = await modelsService.getAvailableModels();
        const textModels = models.filter(m => m.modelType !== 1);
        setAvailableModels(textModels);
        modelsLoadedRef.current = true;
        // Respect pre-selected model from store if set; otherwise default to first
        if (textModels.length > 0) {
          if (storeSelectedModelId) {
            const match = textModels.find(m => m.id === storeSelectedModelId);
            if (match) {
              setSelectedModel(match.name);
            } else if (selectedModel === 'Select option') {
              setSelectedModel(textModels[0].name);
              setLocalSelectedModelId(textModels[0].id);
            }
          } else if (selectedModel === 'Select option') {
            setSelectedModel(textModels[0].name);
            setLocalSelectedModelId(textModels[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to load models:', error);
      } finally {
        setIsLoadingModels(false);
      }
    };

    if (isOpen) {
      loadModels();

      // Calculate optimal modal position
      calculateModalPosition();

      // Add click outside listener
      document.addEventListener('mousedown', handleClickOutside);

      // Add escape key listener
      document.addEventListener('keydown', handleKeyDown);

      // Add scroll listener for gradient effects
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        scrollContainer.addEventListener('scroll', handleScroll);
        // Initial calculation
        handleScroll();
      }

      // Add resize listener to recalculate position
      window.addEventListener('resize', calculateModalPosition);
    } else {
      // Reset the loaded flag when modal closes
      modelsLoadedRef.current = false;
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', calculateModalPosition);
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
    };
  }, [
    isOpen,
    handleClickOutside,
    handleKeyDown,
    handleScroll,
    calculateModalPosition,
  ]);

  // Sync local selected model label with local state when id changes or models list updates
  useEffect(() => {
    if (!availableModels || availableModels.length === 0) return;
    if (!localSelectedModelId) return;
    const match = availableModels.find(m => m.id === localSelectedModelId);
    if (match && selectedModel !== match.name) {
      setSelectedModel(match.name);
    }
  }, [localSelectedModelId, availableModels]);

  // Calculate initial position when component mounts
  useEffect(() => {
    calculateModalPosition();
  }, [calculateModalPosition]);

  // Initialize slider fills only when modal opens
  useEffect(() => {
    if (!isOpen) return undefined;

    // Use a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      const sliders = document.querySelectorAll('input[type="range"]');
      sliders.forEach(slider => {
        const input = slider as HTMLInputElement;
        const value = parseFloat(input.value);
        const min = parseFloat(input.min);
        const max = parseFloat(input.max);
        updateSliderFill(input, value, min, max);
      });
    }, 10);

    return () => clearTimeout(timer);
  }, [isOpen, updateSliderFill]);

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedModelId(storeSelectedModelId);
      setLocalTopP(storeTopP);
      setLocalTopK(storeTopK);
      setLocalTemperature(storeTemperature);

      // Initialize original settings when opening
      setOriginalSettings({
        selectedModelId: storeSelectedModelId,
        topP: storeTopP,
        topK: storeTopK,
        temperature: storeTemperature,
      });
    }
  }, [isOpen, storeSelectedModelId, storeTopP, storeTopK, storeTemperature]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      modelsLoadedRef.current = false;
      mountCountRef.current = 0;
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div
      id="chat-settings-bar"
      ref={settingsBarRef}
      className={`${styles.settingsBar} ${isOpen ? styles.open : ''}`}
      style={{
        bottom: `${anchorBottom ?? modalPosition.bottom}px`,
        right: anchorRight !== undefined ? `${anchorRight}px` : undefined,
        maxHeight: `${modalPosition.maxHeight}px`,
      }}
    >
      <div className={styles.scrollContainer}>
        <div
          className={styles.gradientTop}
          style={{ opacity: topGradientOpacity }}
        ></div>
        <div ref={scrollContainerRef} className={styles.autoLayoutVertical}>
          {/* Model Selection */}
          <div className={styles.statedefaultAutoLayoutfal}>
            <div className={styles.sizesm}>
              <div className={styles.text}>Model</div>
            </div>
            <div
              ref={modelDropdownRef}
              className={`${styles.dropdownContainer} ${isModelDropdownOpen ? styles.open : ''}`}
            >
              <div
                className={styles.sizemdTypedefault}
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
              >
                <div className={styles.text1}>
                  <div className={styles.text2}>{selectedModel}</div>
                </div>
                <img
                  className={styles.rightIcon}
                  alt=""
                  src="/icons/chevron_down.svg"
                  style={{
                    transform: isModelDropdownOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                  }}
                />
              </div>
              {isModelDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {availableModels.length > 0 ? (
                    availableModels.map(model => (
                      <div
                        key={model.id}
                        className={styles.dropdownItem}
                        onClick={() => {
                          setSelectedModel(model.name);
                          setLocalSelectedModelId(model.id);
                          setIsModelDropdownOpen(false);
                        }}
                      >
                        {model.name}
                      </div>
                    ))
                  ) : (
                    <div
                      className={styles.dropdownItem}
                      style={{ opacity: 0.6, cursor: 'default' }}
                    >
                      Loading models...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tools Selection */}
          <div className={styles.statedefaultAutoLayoutfal1}>
            <div className={styles.sizesm}>
              <div className={styles.text}>Tools</div>
            </div>
            <div
              ref={toolsDropdownRef}
              className={`${styles.dropdownContainer} ${isToolsDropdownOpen ? styles.open : ''}`}
            >
              <div
                className={styles.sizemdTypedefault}
                onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
              >
                <div className={styles.text1}>
                  <div className={styles.text2}>{selectedTools}</div>
                </div>
                <img
                  className={styles.rightIcon}
                  alt=""
                  src="/icons/chevron_down.svg"
                  style={{
                    transform: isToolsDropdownOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)',
                  }}
                />
              </div>
              {isToolsDropdownOpen && (
                <div className={styles.dropdownMenu}>
                  {toolsList.map(tool => (
                    <div
                      key={tool}
                      className={styles.dropdownItem}
                      onClick={() => {
                        setSelectedTools(tool);
                        setIsToolsDropdownOpen(false);
                      }}
                    >
                      {tool}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* System Prompt  */}
          {/* <div className={styles.statedefault}>
            <div className={styles.sizesm}>
              <div className={styles.text}>System prompt</div>
            </div>
            <div className={styles.textareaContainer}>
              <textarea
                ref={textareaRef}
                className={styles.systemPromptTextarea}
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                placeholder="Enter the system prompt"
                rows={3}
              />
            </div>
          </div> */}

          {/* TopP */}
          <div className={styles.autoLayoutVertical1}>
            <div className={styles.autoLayoutHorizontal}>
              <div className={styles.sizesm3}>
                <div className={styles.text}>TopP</div>
                <img className={styles.iconHelp} alt="" src="/icons/help.svg" />
              </div>
              <div className={styles.statedefault1}>
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={localTopP}
                  onChange={e => setLocalTopP(parseFloat(e.target.value) || 0)}
                  className={styles.parameterInput}
                />
              </div>
            </div>
            <div className={styles.sliderContainer}>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={localTopP}
                onChange={e => {
                  const value = parseFloat(e.target.value);
                  setLocalTopP(value);
                  updateSliderFill(e.target, value, 0, 1);
                }}
                className={styles.slider}
              />
            </div>
          </div>

          {/* TopK */}
          <div className={styles.autoLayoutVertical2}>
            <div className={styles.autoLayoutHorizontal}>
              <div className={styles.sizesm3}>
                <div className={styles.text}>TopK</div>
                <img className={styles.iconHelp} alt="" src="/icons/help.svg" />
              </div>
              <div className={styles.statedefault1}>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  value={localTopK}
                  onChange={e => setLocalTopK(parseInt(e.target.value) || 0)}
                  className={styles.parameterInput}
                />
              </div>
            </div>
            <div className={styles.sliderContainer}>
              <input
                type="range"
                min="0"
                max="100"
                step="1"
                value={localTopK}
                onChange={e => {
                  const value = parseInt(e.target.value);
                  setLocalTopK(value);
                  updateSliderFill(e.target, value, 0, 100);
                }}
                className={styles.slider}
              />
            </div>
          </div>

          {/* Temperature */}
          <div className={styles.autoLayoutVertical3}>
            <div className={styles.autoLayoutHorizontal}>
              <div className={styles.sizesm3}>
                <div className={styles.text}>Temperature</div>
                <img className={styles.iconHelp} alt="" src="/icons/help.svg" />
              </div>
              <div className={styles.statedefault1}>
                <input
                  type="number"
                  min="0"
                  max="2"
                  step="0.01"
                  value={localTemperature}
                  onChange={e =>
                    setLocalTemperature(parseFloat(e.target.value) || 0)
                  }
                  className={styles.parameterInput}
                />
              </div>
            </div>
            <div className={styles.sliderContainer}>
              <input
                type="range"
                min="0"
                max="2"
                step="0.01"
                value={localTemperature}
                onChange={e => {
                  const value = parseFloat(e.target.value);
                  setLocalTemperature(value);
                  updateSliderFill(e.target, value, 0, 2);
                }}
                className={styles.slider}
              />
            </div>
          </div>

          {/* GPU Layers  */}
          {/* <div className={styles.autoLayoutVertical4}>
            <div className={styles.autoLayoutHorizontal}>
              <div className={styles.sizesm3}>
                <div className={styles.text}>GPU layers</div>
                <img className={styles.iconHelp} alt="" src="/icons/help.svg" />
              </div>
              <div className={styles.statedefault1}>
                <input
                  type="number"
                  min="0"
                  max="50"
                  step="1"
                  value={gpuLayers}
                  onChange={(e) => setGpuLayers(parseInt(e.target.value) || 0)}
                  className={styles.parameterInput}
                />
              </div>
            </div>
            <div className={styles.sliderContainer}>
                             <input
                 type="range"
                 min="0"
                 max="50"
                 step="1"
                 value={gpuLayers}
                 onChange={(e) => {
                   const value = parseInt(e.target.value);
                   setGpuLayers(value);
                   updateSliderFill(e.target, value, 0, 50);
                 }}
                 className={styles.slider}
               />
            </div>
          </div> */}

          {/* Context Size  */}
          {/* <div className={styles.statedefault5}>
            <div className={styles.sizesm}>
              <div className={styles.text}>Context size</div>
            </div>
            <input
              type="number"
              min="512"
              max="32768"
              step="1"
              value={contextSize}
              onChange={(e) => setContextSize(parseInt(e.target.value) || 2150)}
              className={styles.contextSizeInput}
            />
          </div> */}

          {/* Function Support  */}
          {/* <div className={styles.autoLayoutVertical8}>
            <div className={styles.sizesm}>
              <div className={styles.text}>Function support</div>
            </div>
            <div className={styles.autoLayoutHorizontal9}>
              <div className={styles.false}>False</div>
              <div className={styles.sizesmStateinactive}>
                <div className={styles.frame}>
                  <div className={styles.toggle}>
                    <div className={styles.base} />
                    <div className={styles.innerShape} />
                  </div>
                </div>
              </div>
              <div className={styles.false}>True</div>
            </div>
          </div> */}

          <div className={styles.autoLayoutVerticalChild} />
        </div>
        <div
          className={styles.gradientBottom}
          style={{ opacity: bottomGradientOpacity }}
        ></div>
      </div>

      {/* Apply Button */}
      <div
        className={`${styles.sizemdTypeprimaryS} ${!hasSettingsChanged() ? styles.disabled : ''}`}
        onClick={handleApply}
        style={{ cursor: hasSettingsChanged() ? 'pointer' : 'not-allowed' }}
      >
        <div className={styles.autoLayoutHorizontal10}>
          <div className={styles.text28}>Apply</div>
        </div>
      </div>
    </div>
  );
};

export default SettingsBar;
